/**
 * Best Alternative Feature Models
 * Defines data structures for transport alternative analysis
 */

/**
 * User-configurable filters for finding best transport alternatives
 */
export interface BestAlternativeFilters {
  /** Current transport mode being compared against (default: 'Auto') */
  current_transport: string;

  /** Maximum percentage increase in time allowed (e.g., 20 = 20% slower) */
  max_time_percentage: number;

  /** Maximum additional minutes allowed compared to current transport */
  max_additional_minutes: number;

  /** Maximum number of transport legs/changes allowed (optional) */
  max_legs?: number;

  /** Metric to optimize for ('time', 'cost', 'co2', 'sport') */
  selected_metric: string;

  /** Minimum improvement percentage required for the selected metric */
  metric_improvement_percentage: number;

  /** Whether to enforce metric improvement constraints (optional) */
  enforce_metric_constraints?: boolean;
}

/**
 * Result data for a single employee address alternative analysis
 */
export interface BestAlternativeResult {
  /** Address identifier from database */
  address_id: number;

  /** Geographic coordinates for map display */
  geo: {
    type: "Point";
    coordinates: [number, number]; // [longitude, latitude]
  };

  /** Originally used transport mode */
  current_transport: string;

  /** Recommended alternative transport mode */
  recommended_transport: string;

  /** Metric type used for optimization */
  metric_type: string;

  /** Current transport metric value */
  current_value: number;

  /** Alternative transport metric value */
  alternative_value: number;

  /** Percentage improvement achieved */
  improvement_percentage: number;

  /** Whether alternative meets all user constraints */
  meets_constraints: boolean;

  /** Number of transport legs/changes (optional) */
  leg_count?: number;
}

/**
 * Detail of a single acceptable alternative (used by MultipleAlternatives map)
 */
export interface AlternativeDetail {
  transport: string;
  value: number;
  improvement: number;
}

/**
 * Extended result that includes all acceptable alternatives for an address
 */
export interface MultipleAlternativesResult extends BestAlternativeResult {
  alternative_count: number;
  all_alternatives: AlternativeDetail[];
}

/**
 * API response structure for best alternative analysis
 */
export interface BestAlternativeResponse {
  /** Array of alternative results per employee address */
  results: BestAlternativeResult[];

  /** Analysis metadata */
  metadata?: {
    total_addresses: number;
    addresses_with_alternatives: number;
    addresses_fallback_to_current: number;
    filters_applied: BestAlternativeFilters;
  };
}

/**
 * Transport mode configuration for UI dropdowns
 */
export interface TransportModeOption {
  text: string; // Display name (e.g., "Auto")
  value: string; // API value (e.g., "Auto")
  icon?: string; // Material icon name (optional)
}

/**
 * Metric configuration for optimization selection
 */
export interface MetricOption {
  text: string; // Display name (e.g., "Zeit")
  value: string; // API value (e.g., "time")
  unit?: string; // Unit display (e.g., "min", "€", "kg CO2")
  direction: "lower_better" | "higher_better"; // Optimization direction
}

/**
 * Default filter values for initialization
 */
export const DEFAULT_BEST_ALTERNATIVE_FILTERS: BestAlternativeFilters = {
  current_transport: "Auto",
  max_time_percentage: 20,
  max_additional_minutes: 20,
  max_legs: undefined,
  selected_metric: "co2",
  metric_improvement_percentage: 50,
  enforce_metric_constraints: false,
};

/**
 * Available transport modes for selection
 */
export const TRANSPORT_MODE_OPTIONS: TransportModeOption[] = [
  { text: "Pkw", value: "Auto", icon: "directions_car" },
  { text: "E-Auto", value: "E-Auto", icon: "electric_car" },
  { text: "ÖV", value: "ÖV", icon: "directions_bus" },
  { text: "Fahrrad", value: "Fahrrad", icon: "directions_bike" },
  { text: "Pedelec", value: "Pedelec", icon: "electric_bike" },
  { text: "Fußweg", value: "Fuss_Weg", icon: "directions_walk" },
  { text: "Park & Ride", value: "Park_and_Ride", icon: "directions_park" },
  { text: "Bike & Ride", value: "Bike_and_Ride", icon: "directions_bike" },
  { text: "Werksbus", value: "Werksbus", icon: "airport_shuttle" },
  {
    text: "Bike & Werksbus",
    value: "Bike_and_Werksbus",
    icon: "airport_shuttle",
  },
  {
    text: "Bahnreaktivierung & Ride",
    value: "Bahnreaktivierung & Ride",
    icon: "directions_bus",
  },
  {
    text: "Park & Ride & Bike",
    value: "Park_Ride_and_Bike",
    icon: "commute",
  },
];

/**
 * Available metrics for optimization
 */
export const METRIC_OPTIONS: MetricOption[] = [
  { text: "Zeit", value: "time", unit: "min", direction: "lower_better" },
  { text: "Kosten", value: "cost", unit: "€", direction: "lower_better" },
  { text: "CO2", value: "co2", unit: "kg CO2", direction: "lower_better" },
  { text: "Sport", value: "sport", unit: "kcal", direction: "higher_better" },
];
